import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/purview/compliance-tag'

export const purviewRetentionLabelApi = createApi({
  reducerPath: 'purviewRetentionLabel',
  tagTypes: ['RetentionLabel', 'RetentionLabels', 'RetentionLabelsCount'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getRetentionLabels: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => response ?? [],
      providesTags: (result, error, customerId) => [{ type: 'RetentionLabels', id: customerId }],
    }),
    getRetentionLabelById: builder.query({
      query: ({ customerId, labelId }) => ({
        url: `${baseUrl}/${labelId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { labelId }) => [{ type: 'RetentionLabel', id: labelId }],
    }),
    createRetentionLabel: builder.mutation({
      query: ({ customerId, payload }) => ({
        url: baseUrl,
        body: payload,
        params: { customerId },
        method: 'POST',
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'RetentionLabels', id: customerId },
        { type: 'RetentionLabelsCount', id: customerId },
      ],
    }),
    updateRetentionLabel: builder.mutation({
      query: ({ customerId, labelId, payload }) => ({
        url: `${baseUrl}/${labelId}`,
        body: payload,
        params: { customerId },
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { customerId, labelId }) => [
        { type: 'RetentionLabels', id: customerId },
        { type: 'RetentionLabel', id: labelId },
        { type: 'RetentionLabelsCount', id: customerId },
      ],
    }),
    deleteRetentionLabel: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'DELETE',
        params: { customerId },
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'RetentionLabels', id: customerId },
        { type: 'RetentionLabelsCount', id: customerId },
      ],
    }),
    getRetentionLabelsCount: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId, countOnly: true },
      }),
      transformResponse: (response) => response?.count ?? 0,
      providesTags: (result, error, customerId) => [{ type: 'RetentionLabelsCount', id: customerId }],
    }),
  }),
})

export const {
  useGetRetentionLabelsQuery,
  useLazyGetRetentionLabelsQuery,
  useGetRetentionLabelByIdQuery,
  useLazyGetRetentionLabelByIdQuery,
  useGetRetentionLabelsCountQuery,
  useLazyGetRetentionLabelsCountQuery,
  useCreateRetentionLabelMutation,
  useUpdateRetentionLabelMutation,
  useDeleteRetentionLabelMutation,
} = purviewRetentionLabelApi

export const useGetRetentionLabels = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionLabelsQuery(customerId, { ...options, skip: !customerId || options.skip })
}

export const useGetRetentionLabelById = ({ labelId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionLabelByIdQuery(
    { customerId, labelId },
    { ...options, skip: !customerId || !labelId || options.skip },
  )
}
